// Metronome: a synthesized click sprite (accent + normal) played through its
// own gain node. The render function is pure; `Metronome` wraps Web Audio.

import { Biquad, addDampedSine, newPcm, noiseGen, normalize } from './dsp';
import { buildSprite, type SpriteData } from './sprite';
import { SYNTH_SAMPLE_RATE } from './synth';

/** Sprite keys of the two clicks. */
export const CLICK_NORMAL = 0;
export const CLICK_ACCENT = 1;

/** Short woodblock-like clicks: accent higher and louder. */
export function renderClickSprite(sr = SYNTH_SAMPLE_RATE): SpriteData {
  const make = (f: number, level: number, seed: number) => {
    const out = newPcm(0.07 * sr);
    addDampedSine(out, sr, f, 1, 0.05, 0);
    addDampedSine(out, sr, f * 2.01, 0.35, 0.03, 0);
    addDampedSine(out, sr, f * 3.4, 0.12, 0.02, 0);
    const rnd = noiseGen(seed);
    const bp = Biquad.bandpass(sr, f * 2, 1.2);
    for (let i = 0; i < 0.004 * sr; i++) out[i] += bp.process(rnd()) * 0.6 * (1 - i / (0.004 * sr));
    normalize(out, level);
    return out;
  };
  return buildSprite(
    [
      { key: CLICK_NORMAL, data: make(1250, 0.6, 7) },
      { key: CLICK_ACCENT, data: make(1900, 0.9, 11) },
    ],
    sr,
    true,
    0.01,
  );
}

let clickSprite: SpriteData | null = null;

function spriteBuffer(ctx: BaseAudioContext): { buffer: AudioBuffer; sprite: SpriteData } {
  clickSprite ??= renderClickSprite();
  const buffer = ctx.createBuffer(1, clickSprite.data.length, clickSprite.sampleRate);
  buffer.copyToChannel(clickSprite.data, 0);
  return { buffer, sprite: clickSprite };
}

/** Metronome clicks on a context; owns a volume gain node connected to `destination`. */
export class Metronome {
  readonly output: GainNode;
  private readonly buffer: AudioBuffer;
  private readonly sprite: SpriteData;
  private volume = 0.7;

  constructor(
    private readonly ctx: BaseAudioContext,
    destination: AudioNode,
  ) {
    const { buffer, sprite } = spriteBuffer(ctx);
    this.buffer = buffer;
    this.sprite = sprite;
    this.output = ctx.createGain();
    this.output.gain.value = this.volume * this.volume * 1.4;
    this.output.connect(destination);
  }

  /** 0…1 (quadratic taper). */
  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    const g = this.volume * this.volume * 1.4;
    this.output.gain.setTargetAtTime(g, this.ctx.currentTime, 0.01);
  }

  getVolume(): number {
    return this.volume;
  }

  /** Schedule a click; returns the source so it can be cancelled. */
  click(when: number, accent: boolean): AudioBufferSourceNode {
    const e = this.sprite.map[String(accent ? CLICK_ACCENT : CLICK_NORMAL)];
    const src = this.ctx.createBufferSource();
    src.buffer = this.buffer;
    src.connect(this.output);
    src.start(Math.max(when, this.ctx.currentTime), e[0], e[1]);
    src.onended = () => src.disconnect();
    return src;
  }

  dispose(): void {
    this.output.disconnect();
  }
}
